import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { useAuth } from '../components/shared/AuthContext';
import { LogoLoader } from "../components/ui";

const J = { primary: "#3F51B5", primaryDk: "#303F9F", accent: "#2196F3", light: "#c3dbeb", lightBg: "#EEF2FB", success: "#27ae60", danger: "#e74c3c", text: "#1a237e" };

const LedgerGroup = () => {
  const { data, roleName, jwt } = useAuth();
  const branchId     = data?.branchId || null;
  const isSuperAdmin = roleName === "superadmin";

  const [ledgerGroups,  setLedgerGroups]  = useState([]);
  const [accountGroups, setAccountGroups] = useState([]);
  // Only a superadmin picks a branch; see the Branch field in the form below.
  const [branches,      setBranches]      = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [showModal,     setShowModal]     = useState(false);
  const [editingItem,   setEditingItem]   = useState(null);
  const [formData,      setFormData]      = useState({ account_group_id: "", ledger_group: "", opening_balance: "", opening_date: new Date().toISOString().split("T")[0], counter_account_id: "", branch_choice: "" });
  const [search,        setSearch]        = useState("");
  const [rowsPerPage,   setRowsPerPage]   = useState(5);
  const [currentPage,   setCurrentPage]   = useState(1);

  useEffect(() => { loadAll(); }, []);

  // Reads ledger_account under base_ledger_group, NOT ledger_group under
  // account_group. Those two tables were built but never populated (0 rows), so
  // this page showed nothing. base_ledger_group -> ledger_account is the
  // structure wired to ledger_transaction and therefore to the day-end.
  //
  // Field mapping: ledger_account.name -> ledger_group,
  //                ledger_account.ledger_id -> account_group_id.
  const loadAll = async () => {
    setLoading(true);
    try {
      // Branch-scoped for ordinary users; shared accounts (branchId NULL) are
      // always visible because they belong to no single branch.
      const [laRes, bgRes, brRes] = await Promise.all([
        axios.get("/api/getModelData/ledger_account",     { baseURL: API_BASE_URL, params: { filter: "{}" } }),
        axios.get("/api/getModelData/base_ledger_group",  { baseURL: API_BASE_URL, params: { filter: "{}" } }),
        // Only a superadmin is offered the choice, but the list is cheap and
        // fetching it conditionally would mean a second load path to keep right.
        axios.get("/api/getModelData/branch_details",     { baseURL: API_BASE_URL, params: { filter: "{}" } }),
      ]);
      const toArr = r => Array.isArray(r.data?.data) ? r.data.data : Array.isArray(r.data) ? r.data : [];
      setBranches(toArr(brRes));

      const accounts = toArr(laRes).filter(
        a => isSuperAdmin || !branchId || a.branchId == null || Number(a.branchId) === Number(branchId)
      );

      setLedgerGroups(
        accounts.map(a => ({
          id: a.id,
          ledger_group: a.name,
          account_group_id: a.ledger_id,
          is_default: 0,
          branchId: a.branchId,
        }))
      );
      setAccountGroups(
        toArr(bgRes).map(g => ({ id: g.id, account_group: g.group_name, base_group: g.group_type }))
      );
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // Which side of Assets = Liabilities + Equity an account group sits on.
  // Classified by GROUP TYPE, never by name: "Loans Outstanding - FEDERAL BANK"
  // and "Borrowings - ESAF BANK" both contain "bank" while being an asset and a
  // liability, and name matching is what put a cash figure into a bank account.
  const sideOfGroup = (accountGroupId) => {
    const g = accountGroups.find(a => Number(a.id) === Number(accountGroupId));
    const t = String(g?.base_group || "").trim().toUpperCase();
    if (t === "ASSET") return "left";
    if (t === "LIABILITY" || t === "EQUITY") return "right";
    return null;
  };

  // Accounts that could balance the one being edited — the opposite side, same
  // branch, excluding itself.
  const counterpartOptions = () => {
    const side = sideOfGroup(formData.account_group_id);
    if (!side) return [];
    return ledgerGroups
      .filter(a => a.id !== editingItem?.id)
      .filter(a => sideOfGroup(a.account_group_id) && sideOfGroup(a.account_group_id) !== side)
      .filter(a => a.branchId == null || !branchId || Number(a.branchId) === Number(branchId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.account_group_id || !formData.ledger_group.trim()) return;

    // WHICH BRANCH OWNS THIS ACCOUNT.
    //
    // `branchId` here is the logged-in user's, and a SUPERADMIN'S IS NULL — so
    // every account a superadmin created was stored with no branch, and every
    // branch-scoped reader treats NULL as "shared, show to everyone". Two `kvb`
    // accounts made that way appeared in Palani's Ledger Report alongside its
    // own, looking like a broken branch filter.
    //
    // A superadmin now chooses explicitly. "shared" is still available, but it
    // has to be said rather than fallen into.
    let ownerBranchId = branchId;
    if (isSuperAdmin) {
      if (!formData.branch_choice) return;      // the submit button is disabled too
      ownerBranchId = formData.branch_choice === "shared"
        ? null
        : Number(formData.branch_choice);
    }

    setLoading(true);
    try {
      const payload = {
        type: editingItem ? "Edit" : "Add",
        model: "ledger_account",
        name: formData.ledger_group.trim(),
        ledger_id: Number(formData.account_group_id),
        account_type: "1",
        branchId: ownerBranchId,
      };
      if (editingItem) payload.id = editingItem.id;
      const saveRes = await axios.post("/api/addDetails", payload, { baseURL: API_BASE_URL });

      // Post the opening balance to the account just saved. The endpoint updates
      // an existing entry for the same account and date rather than duplicating,
      // so re-saving corrects the figure instead of compounding it. Zero removes
      // the entry outright.
      //
      // An empty field is treated as zero when editing an account that already
      // has a balance: clearing the box is how an operator expresses "remove
      // this". Skipping the call in that case leaves the old figure in place
      // while the form reports success — which is how a Rs50,00,000 test seed
      // survived being cleared twice.
      const rawOpening = String(formData.opening_balance).trim();
      const isEditingExisting = Boolean(editingItem);
      const shouldPostOpening = rawOpening !== "" || isEditingExisting;

      if (shouldPostOpening) {
        const openingAmount = rawOpening === "" ? 0 : Number(rawOpening);
        const accountId = editingItem ? editingItem.id : saveRes.data?.savedData?.id;
        if (accountId && Number.isFinite(openingAmount) && openingAmount >= 0) {
          await axios.post(
            "/api/ledger/opening-balance",
            {
              // The account's branch, not the user's. A superadmin creating a
              // Palani account must post its opening balance to Palani — posting
              // it under their own NULL branch would strand the entry.
              branchId: ownerBranchId,
              accountId: Number(accountId),
              date: formData.opening_date,
              amount: openingAmount,
              // The other side of the entry. Required by the server whenever the
              // amount is above zero — an opening balance with one leg leaves the
              // branch permanently out of balance by that amount.
              counterAccountId: formData.counter_account_id
                ? Number(formData.counter_account_id)
                : null,
              narration: "Entered on ledger account setup",
            },
            // Guarded by AdminOrSuperAdmin - the token is required or this 401s.
            { baseURL: API_BASE_URL, headers: { "x-token": jwt } }
          );
        }
      }

      await loadAll();
      closeModal();
    } catch (err) {
      alert(err.response?.data?.error || "Error saving. Please try again.");
    }
    setLoading(false);
  };

  const openAdd  = () => { setEditingItem(null); setFormData({ account_group_id: "", ledger_group: "", opening_balance: "", opening_date: new Date().toISOString().split("T")[0], counter_account_id: "", branch_choice: "" }); setShowModal(true); };
  const openEdit = async item => {
    const today = new Date().toISOString().split("T")[0];

    // Clear the form and open the modal BEFORE fetching the balance.
    // Awaiting first left the previous account's amount on screen — an account
    // with no balance never overwrote it, so a cash figure was submitted against
    // a bank account.
    setEditingItem(item);
    setFormData({
      account_group_id: item.account_group_id || "",
      ledger_group: item.ledger_group || "",
      opening_balance: "",
      opening_date: today,
      counter_account_id: "",
      // Prefilled from the row, so editing an account does not silently move it
      // to another branch — or force the superadmin to re-pick one they already
      // chose. A NULL branchId on the record IS "shared", so it maps there.
      branch_choice: item.branchId == null ? "shared" : String(item.branchId),
    });
    setShowModal(true);

    // Then fill in this account's own balance, if it has one.
    try {
      const res = await axios.get("/api/ledger/opening-balance", {
        baseURL: API_BASE_URL,
        params: { branchId: item.branchId || branchId, date: today, accountId: item.id },
      });
      // `ledgerBalance` is the stored seed; `balance` adds movement recorded in
      // `transactions` but not yet posted to the ledger. This form EDITS the seed,
      // so it must show the seed — prefilling the derived figure and re-submitting
      // would fold that movement into the seed and count it twice on the next read.
      // Falls back to `balance` for a server that predates the split.
      const data = res.data?.data;
      // openingSeed is the unsigned amount entered; ledgerBalance is debit-side and
      // reads negative for a liability now that opening legs sit on their natural side.
      const seed = data?.openingSeed !== undefined && data?.openingSeed !== null
        ? data.openingSeed
        : data?.ledgerBalance !== undefined ? data.ledgerBalance : data?.balance;
      setFormData(p => ({
        ...p,
        ...(seed ? { opening_balance: String(seed) } : {}),
        // Show the account this balance was paired with, so an existing pairing is
        // visible and correctable rather than silently re-chosen on every edit.
        ...(data?.counterAccountId ? { counter_account_id: String(data.counterAccountId) } : {}),
      }));
    } catch { /* account may simply have no opening balance yet */ }
  };
  const closeModal = () => { setShowModal(false); setEditingItem(null); setFormData({ account_group_id: "", ledger_group: "", opening_balance: "", opening_date: new Date().toISOString().split("T")[0], counter_account_id: "", branch_choice: "" }); };

  const getAGName = (id) => { const ag = accountGroups.find(a => a.id === id || a.id === Number(id)); return ag ? ag.account_group : "—"; };

  const filtered   = ledgerGroups.filter(g => (g.ledger_group || "").toLowerCase().includes(search.toLowerCase()) || getAGName(g.account_group_id).toLowerCase().includes(search.toLowerCase()));
  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const paginated  = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  return (
    <div style={{ background: "transparent", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ background: "transparent", padding: "4px 0", marginBottom: 20, boxShadow: "none" }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h5 className="mb-0 fw-bold" style={{ color: "#004BB5", fontSize: "1.15rem" }}>
              <i className="fas fa-sitemap me-2" />Ledger Group
            </h5>
          </div>
          <div className="d-flex gap-2 align-items-center" style={{ flexWrap: "nowrap" }}>
            <input
              type="text"
              placeholder="Search ledger group..."
              value={search}
              onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
              style={{
                borderRadius: 8,
                border: "1px solid #CBD5E1",
                background: "#fff",
                color: "#1E293B",
                padding: "4px 12px",
                height: 32,
                outline: "none",
                width: 220,
                fontSize: "0.84rem"
              }}
            />
            <button
              onClick={openAdd}
              style={{
                background: "#004BB5",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "0 16px",
                height: 32,
                fontWeight: 600,
                fontSize: "0.84rem",
                cursor: "pointer",
                whiteSpace: "nowrap",
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              + Add
            </button>
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <div className="table-responsive">
          <table className="table mb-0" style={{ fontSize: "0.88rem" }}>
            <thead style={{ background: J.primary, color: "#fff" }}>
              <tr>
                <th style={{ padding: "12px 16px", fontWeight: 600 }}>#</th>
                <th style={{ padding: "12px 16px", fontWeight: 600 }}>Ledger Account</th>
                <th style={{ padding: "12px 16px", fontWeight: 600 }}>Account Group</th>
                <th style={{ padding: "12px 16px", fontWeight: 600 }}>Branch</th>
                <th style={{ padding: "12px 16px", fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-6"><LogoLoader size="md" /></td></tr>
              ) : paginated.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-5 text-muted">No ledger accounts found. Click <strong>+ Add</strong> to create one.</td></tr>
              ) : paginated.map((item, idx) => (
                <tr key={item.id || idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ padding: "12px 16px", color: "#999", fontSize: "0.82rem" }}>{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                  <td style={{ padding: "12px 16px", fontWeight: 700, color: J.text }}>{item.ledger_group}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{ background: J.lightBg, color: J.primary, border: `1px solid ${J.light}`, borderRadius: 6, padding: "3px 10px", fontSize: "0.78rem", fontWeight: 600 }}>
                      {getAGName(item.account_group_id)}
                    </span>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{ background: item.branchId ? "#e8f5e9" : "#fafafa", color: item.branchId ? J.success : "#aaa", borderRadius: 6, padding: "3px 10px", fontSize: "0.78rem" }}>
                      {item.branchId ? `Branch ${item.branchId}` : "Shared"}
                    </span>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <button onClick={() => openEdit(item)} style={{ background: J.lightBg, border: `1px solid ${J.primary}`, color: J.primary, borderRadius: 6, padding: "4px 12px", cursor: "pointer", fontSize: "0.82rem" }}>
                      <i className="fas fa-edit" /> Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={{ padding: "6px 16px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
          <div className="d-flex align-items-center gap-2 flex-nowrap" style={{ whiteSpace: "nowrap" }}>
            <span style={{ fontSize: "0.82rem", color: "#888", whiteSpace: "nowrap" }}>Rows per page:</span>
            <select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              style={{ border: `1px solid ${J.light}`, borderRadius: 6, padding: "2px 6px", height: 28, fontSize: "0.82rem", outline: "none" }}>
              {[5, 10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="d-flex gap-1 flex-nowrap">
            {["First", "Prev", "Next", "Last"].map((lbl, i) => {
              const disabled = i < 2 ? currentPage === 1 : currentPage === totalPages;
              const onClick  = [() => setCurrentPage(1), () => setCurrentPage(p => Math.max(1, p - 1)), () => setCurrentPage(p => Math.min(totalPages, p + 1)), () => setCurrentPage(totalPages)][i];
              return <button key={lbl} onClick={onClick} disabled={disabled} style={{ border: `1px solid ${disabled ? "#ddd" : J.primary}`, background: lbl === "Next" ? J.primary : "#fff", color: lbl === "Next" ? "#fff" : disabled ? "#ccc" : J.primary, borderRadius: 6, padding: "3px 10px", height: 28, fontSize: "0.82rem", cursor: disabled ? "not-allowed" : "pointer" }}>{lbl}</button>;
            })}
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 480, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", overflow: "hidden" }}>
            <div style={{ background: `linear-gradient(135deg, ${J.primary}, ${J.accent})`, padding: "16px 20px", color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              {/* Name the account being edited. A generic title gave no clue which
                  row was open, which is how a cash figure was saved to a bank account. */}
              <h6 className="mb-0 fw-bold">
                {editingItem
                  ? `Edit Ledger Account — ${editingItem.ledger_group}`
                  : "Add Ledger Account"}
              </h6>
              <button onClick={closeModal} style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", borderRadius: 6, width: 28, height: 28, cursor: "pointer" }}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ padding: "20px" }}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", fontWeight: 600, marginBottom: 6, fontSize: "0.88rem", color: J.text }}>Account Group</label>
                  <select value={formData.account_group_id} onChange={e => setFormData(p => ({ ...p, account_group_id: e.target.value }))} required
                    style={{ width: "100%", border: `1px solid ${J.light}`, borderRadius: 8, padding: "9px 12px", fontSize: "0.88rem", outline: "none" }}>
                    <option value="">-- Select Account Group --</option>
                    {accountGroups.map(ag => <option key={ag.id} value={ag.id}>{ag.account_group} ({ag.base_group})</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", fontWeight: 600, marginBottom: 6, fontSize: "0.88rem", color: J.text }}>Ledger Account Name</label>
                  <input type="text" value={formData.ledger_group} onChange={e => setFormData(p => ({ ...p, ledger_group: e.target.value }))} required placeholder="e.g. Cash in Hand, SBI Bank"
                    style={{ width: "100%", border: `1px solid ${J.light}`, borderRadius: 8, padding: "9px 12px", fontSize: "0.88rem", outline: "none", boxSizing: "border-box" }} />
                </div>

                {/* SUPERADMIN ONLY. An admin's account takes their own branch
                    automatically, as before — no extra field to fill.

                    A superadmin has no branch of their own, so without this the
                    account was saved with branchId NULL and every branch saw it.
                    No default is pre-selected: "shared" must be chosen, not
                    arrived at by leaving the field alone. */}
                {isSuperAdmin && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: "block", fontWeight: 600, marginBottom: 6, fontSize: "0.88rem", color: J.text }}>Branch</label>
                    <select value={formData.branch_choice} onChange={e => setFormData(p => ({ ...p, branch_choice: e.target.value }))} required
                      style={{ width: "100%", border: `1px solid ${J.light}`, borderRadius: 8, padding: "9px 12px", fontSize: "0.88rem", outline: "none" }}>
                      <option value="">-- Select Branch --</option>
                      {branches.map(b => (
                        <option key={b.id} value={b.id}>{b.branchName}</option>
                      ))}
                      <option value="shared">Shared — all branches</option>
                    </select>
                    <div style={{ fontSize: "0.78rem", color: "#666", marginTop: 4 }}>
                      Shared accounts appear in every branch's reports. Choose a
                      branch unless the account genuinely belongs to all of them.
                    </div>
                  </div>
                )}

                {/* Opening balance captured at account creation. Entering it here
                    avoids a separate trip to the Receipts screen, and posts the
                    same ledger entry a receipt would. */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", fontWeight: 600, marginBottom: 6, fontSize: "0.88rem", color: J.text }}>
                    Opening Balance (Rs) <span style={{ fontWeight: 400, color: "#888" }}>— optional</span>
                  </label>
                  <input type="number" min="0" step="0.01" value={formData.opening_balance}
                    onChange={e => setFormData(p => ({ ...p, opening_balance: e.target.value }))}
                    placeholder="Leave blank to start at zero"
                    style={{ width: "100%", border: `1px solid ${J.light}`, borderRadius: 8, padding: "9px 12px", fontSize: "0.88rem", outline: "none", boxSizing: "border-box" }} />
                </div>

                {formData.opening_balance !== "" && Number(formData.opening_balance) >= 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: "block", fontWeight: 600, marginBottom: 6, fontSize: "0.88rem", color: J.text }}>Balance As At</label>
                    <input type="date" value={formData.opening_date}
                      onChange={e => setFormData(p => ({ ...p, opening_date: e.target.value }))}
                      style={{ width: "100%", border: `1px solid ${J.light}`, borderRadius: 8, padding: "9px 12px", fontSize: "0.88rem", outline: "none", boxSizing: "border-box" }} />
                    <small style={{ display: "block", marginTop: 6, color: "#666", fontSize: "0.78rem" }}>
                      Re-saving <em>updates</em> this figure rather than adding a second one,
                      so a wrong amount can be corrected at any time.
                    </small>
                  </div>
                )}

                {/* Every opening balance needs an equal entry on the other side of
                    Assets = Liabilities + Equity. Without it the branch is out of
                    balance by exactly this amount, with nothing to show why. */}
                {formData.opening_balance !== "" && Number(formData.opening_balance) > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: "block", fontWeight: 600, marginBottom: 6, fontSize: "0.88rem", color: J.text }}>
                      Balancing Account <span style={{ fontWeight: 400, color: J.danger }}>— required</span>
                    </label>
                    <select value={formData.counter_account_id}
                      onChange={e => setFormData(p => ({ ...p, counter_account_id: e.target.value }))}
                      required
                      style={{ width: "100%", border: `1px solid ${J.light}`, borderRadius: 8, padding: "9px 12px", fontSize: "0.88rem", outline: "none" }}>
                      <option value="">-- Where did this money come from? --</option>
                      {counterpartOptions().map(a => (
                        <option key={a.id} value={a.id}>
                          {a.ledger_group} ({getAGName(a.account_group_id)})
                        </option>
                      ))}
                    </select>
                    <small style={{ display: "block", marginTop: 6, color: "#666", fontSize: "0.78rem" }}>
                      {sideOfGroup(formData.account_group_id) === "left"
                        ? "This account holds money. Choose whose it is — members' savings, a lender, or the FPO's own capital."
                        : "This is money owed or invested. Choose where it is held — cash, bank, or loans given out."}
                      {" "}An equal entry is written there automatically.
                    </small>
                    {counterpartOptions().length === 0 && (
                      <small style={{ display: "block", marginTop: 6, color: J.danger, fontSize: "0.78rem" }}>
                        No account on the opposite side exists for this branch yet.
                      </small>
                    )}
                  </div>
                )}
              </div>
              <div style={{ padding: "12px 20px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "center", gap: 10 }}>
                <button type="button" onClick={closeModal} style={{ border: "1px solid #ddd", background: "#fff", color: "#666", borderRadius: 8, padding: "8px 24px", cursor: "pointer" }}>Cancel</button>
                <button type="submit" disabled={loading} style={{ background: `linear-gradient(135deg, ${J.primary}, ${J.accent})`, border: "none", color: "#fff", borderRadius: 8, padding: "8px 32px", fontWeight: 700, cursor: loading ? "not-allowed" : "pointer" }}>
                  {loading ? "Saving..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LedgerGroup;
